import { NavLink, useNavigate, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { LayoutDashboard, Search, Library, LogOut, Terminal, GitCompareArrows, FileCode2, KanbanSquare, Network as NetworkIcon, Inbox, Settings, Grid3x3, PackageCheck, Euro, Zap } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLocale } from "@/context/LocaleContext";
import { api } from "@/lib/api";
import { loadSeenIds } from "@/pages/Deliveries";

// Admin-only nav (everything that mutates data or runs AI directly).
const ADMIN_ITEMS = [
    { to: "/app", end: true, key: "nav.dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/app/research", key: "nav.research", icon: Search, testid: "nav-research" },
    { to: "/app/library", key: "nav.library", icon: Library, testid: "nav-library" },
    { to: "/app/synergy", key: "nav.synergy", icon: GitCompareArrows, testid: "nav-synergy" },
    { to: "/app/matrix", label: "Matrix", icon: Grid3x3, testid: "nav-matrix" },
    { to: "/app/network", key: "nav.network", icon: NetworkIcon, testid: "nav-network" },
    { to: "/app/board", key: "nav.board", icon: KanbanSquare, testid: "nav-board" },
    { to: "/app/patterns", key: "nav.patterns", icon: FileCode2, testid: "nav-patterns" },
    { to: "/app/requests", label: "Inbox", icon: Inbox, testid: "nav-inbox" },
    { to: "/app/deliveries", label: "Deliveries", icon: PackageCheck, testid: "nav-deliveries", showUnread: true },
    { to: "/app/revenue", label: "Revenue", icon: Euro, testid: "nav-revenue" },
    { to: "/app/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

// Client nav: submit requests + receive deliveries + upgrade + settings.
const CLIENT_ITEMS = [
    { to: "/app/requests", end: true, label: "My requests", icon: Inbox, testid: "nav-requests" },
    { to: "/app/deliveries", label: "Deliveries", icon: PackageCheck, testid: "nav-deliveries", showUnread: true },
    { to: "/app/upgrade", label: "Upgrade", icon: Zap, testid: "nav-upgrade" },
    { to: "/app/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

// Polls /api/requests?status=completed every 45s and computes unseen-since-last-visit.
// "Seen" state lives in localStorage and is updated by Deliveries.jsx on mount.
function useUnreadDeliveries(userId) {
    const [unread, setUnread] = useState(0);

    useEffect(() => {
        if (!userId) return;
        let cancelled = false;

        const refresh = async () => {
            try {
                const { data } = await api.get("/requests?status=completed");
                if (cancelled) return;
                const seen = loadSeenIds(userId);
                const count = (data.requests || []).filter((r) => !seen.has(r.id)).length;
                setUnread(count);
            } catch { /* silent — nav badge is non-critical */ }
        };

        refresh();
        const id = setInterval(refresh, 45_000);
        // Deliveries.jsx fires this event on mount after marking everything seen.
        const onSeen = () => refresh();
        window.addEventListener("affynix:deliveries-seen", onSeen);
        return () => { cancelled = true; clearInterval(id); window.removeEventListener("affynix:deliveries-seen", onSeen); };
    }, [userId]);

    return unread;
}

export default function Sidebar() {
    const { user, logout } = useAuth();
    const { t } = useLocale();
    const nav = useNavigate();
    const doLogout = async () => { await logout(); nav("/login"); };
    const isAdmin = user?.role === "admin";
    const items = isAdmin ? ADMIN_ITEMS : CLIENT_ITEMS;
    const unread = useUnreadDeliveries(user?.id);

    return (
        <aside className="w-64 shrink-0 border-r border-zinc-800 bg-zinc-950 flex flex-col" data-testid="app-sidebar">
            <div className="px-5 py-5 border-b border-zinc-800 flex items-center gap-2.5">
                <Link to={isAdmin ? "/app" : "/app/requests"} className="flex items-center gap-2.5">
                    <Terminal className="w-5 h-5 text-amber-500" />
                    <div>
                        <div className="font-heading font-black tracking-tighter text-lg text-zinc-50">affynix</div>
                        <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 mt-1">{isAdmin ? "research terminal" : "client portal"}</div>
                    </div>
                </Link>
            </div>

            <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
                {items.map((item) => {
                    const Icon = item.icon;
                    const label = item.label || (item.key ? t(item.key) : "");
                    const badge = item.showUnread && unread > 0 ? unread : null;
                    return (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.end}
                            data-testid={item.testid}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2 font-mono text-xs uppercase tracking-wider transition-colors ${
                                    isActive ? "bg-amber-500/10 text-amber-400 border-l-2 border-amber-500" : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 border-l-2 border-transparent"
                                }`
                            }
                        >
                            <Icon className="w-4 h-4" />
                            <span className="flex-1">{label}</span>
                            {badge !== null && (
                                <span
                                    className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-emerald-500 text-black font-bold text-[10px] rounded-full"
                                    data-testid="nav-deliveries-unread"
                                    aria-label={`${badge} unread deliveries`}
                                >
                                    {badge > 99 ? "99+" : badge}
                                </span>
                            )}
                        </NavLink>
                    );
                })}
            </nav>

            <div className="px-3 py-4 border-t border-zinc-800">
                <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                        <div className="font-mono text-[11px] text-zinc-300 truncate">{user?.name || "—"}</div>
                        <div className="text-[10px] text-zinc-500 font-mono truncate">{user?.email} <span className="text-amber-500">· {isAdmin ? "admin" : "client"}</span></div>
                    </div>
                    <button onClick={doLogout} data-testid="logout-btn" className="shrink-0 text-zinc-500 hover:text-amber-500 p-1.5" title="Sign out">
                        <LogOut className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </aside>
    );
}
